import { IRefIds } from 'constants/refs';
import { del, get, post, postFile, put } from 'services/request';
import { IItemId } from 'types/content';
import { IMemoryPhrasebook, IMemoryPhrasebookEdit } from 'types/memoryPhrasebook';
import { BaseModel, IModelOptions, IModuleSearch } from './BaseModel';
import TranslationManagerModel from './TranslationManagerModel';

export interface IPhrasebookImportDiff {
  type: 'notes' | 'dialect';
  phrasebookId: number;
  translationDialectId?: number;
  dialect?: string;
  field: 'notes' | 'text' | 'status';
  oldValue: string;
  newValue: string;
}

export interface IPhrasebookImportValidateResponse {
  result: string;
  diffs: IPhrasebookImportDiff[];
  errors: string[];
}

export interface IPhrasebookImportExecuteEntry {
  translationDialectId: number;
  text?: string;
  status?: string;
}

export interface IPhrasebookImportExecutePayload {
  notes: { phrasebookId: number; newValue: string }[];
  dialects: IPhrasebookImportExecuteEntry[];
}

export default class MemoryPhrasebookModel extends BaseModel<IMemoryPhrasebook, IMemoryPhrasebookEdit> {
  translations: TranslationManagerModel;

  constructor(options: IModelOptions) {
    super(options);
    this.ref = IRefIds.memoryPhrasebook;
    this.gridName = 'MemoryPhrasebook';
    this.getGridColumns();
    this.translations = new TranslationManagerModel({ ref: this.ref });
  }

  async getGrid() {
    await super.getGrid();
    this.gridData = await get<IMemoryPhrasebook[]>(`/memory-phrasebook`);
    this.loadingGrid = false;
    this.render();
  }

  async getGridSearch(text: string, dialect?: string) {
    await super.getGrid();
    if (this.gridColumns.length === 0) {
      await this.getGridColumns();
    }
    const qs = new URLSearchParams();
    if (text) qs.set('text', text);
    if (dialect) qs.set('dialect', dialect);
    this.gridData = await get<IMemoryPhrasebook[]>(`/memory-phrasebook?${qs.toString()}`);
    this.loadingGrid = false;
    this.render();

    if (this.gridData.length > 0) {
      await this.loadItemEdit(this.gridData[0].id);
    }
  }

  async getGridRow(itemId: number) {
    return await get<IMemoryPhrasebook>(`memory-phrasebook/${itemId}/grid-row`);
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async new() {
    await super.new();
    const [item, dialects] = await Promise.all([
      get<IMemoryPhrasebookEdit>(`memory-phrasebook/new`),
      this.translations.new(this.courseId),
    ]);
    item.dialects = dialects;
    await this.setItem(item);
    this.render();
  }

  async save() {
    await super.save();
    const { id } = this.itemUpdated;
    if (id === this.NEW_CREATE_ID) {
      await this.createItem();
    } else {
      await this.updateItem();
    }
  }

  async updateItem() {
    const { id, dialects, example, isInvariable } = this.itemUpdated as IMemoryPhrasebookEdit;
    await put(`/memory-phrasebook/${id}`, { example, isInvariable });
    await this.translations.update(id, dialects);
    await Promise.all([this.updateGridRow(id, 'update'), this.loadItemEdit(id)]);
  }

  async createItem() {
    const { dialects, example, isInvariable } = this.itemUpdated as IMemoryPhrasebookEdit;
    const result = await post<IItemId>(`memory-phrasebook`, { example, isInvariable });

    if (result.ok && result.data) {
      const newItemId = result.data.id;
      await this.translations.update(newItemId, dialects);
      await Promise.all([this.updateGridRow(newItemId, 'add'), this.loadItemEdit(newItemId)]);
    }
  }

  async loadItemEdit(itemId?: number) {
    this.loadingItem = true;
    this.render();
    if (itemId === undefined) {
      itemId = 0;
    }
    this.itemId = itemId;

    const item = await this.loadItemData(itemId);
    await this.setItem(item as IMemoryPhrasebookEdit);

    this.render();
  }

  async delete() {
    await super.delete();
    const { id } = this.itemUpdated;

    const result = await del(`memory-phrasebook/${id}`);
    if (result.ok) {
      await Promise.all([this.updateGridRow(id, 'remove'), this.new()]);
    } else {
      console.error(JSON.stringify(result.data));
    }
  }

  async loadItemData(itemId: number) {
    const [item, dialects] = await Promise.all([
      get<IMemoryPhrasebookEdit>(`memory-phrasebook/${itemId}`),
      this.translations.get(itemId),
    ]);
    item.dialects = dialects;
    return item;
  }

  async search(data: IModuleSearch & { dialect?: string }) {
    const text = data.text ? data.text : '';
    if (text !== '') {
      await this.getGridSearch(text, data.dialect);
    } else {
      await this.getGrid();
    }
  }

  async validateImportCSV(file: File): Promise<IPhrasebookImportValidateResponse> {
    const response = await postFile<IPhrasebookImportValidateResponse>(
      '/memory-phrasebook/import-csv/validate',
      file,
    );
    return response.data || { result: 'ERROR', diffs: [], errors: ['Failed to validate file'] };
  }

  async executeImport(payload: IPhrasebookImportExecutePayload): Promise<{ result: string; updated: number; errors: string[] }> {
    const response = await post<{ result: string; updated: number; errors: string[] }>(
      '/memory-phrasebook/import-csv/execute',
      payload,
    );
    return response.data || { result: 'ERROR', updated: 0, errors: ['Failed to execute import'] };
  }
}
